/**
 * The Package panel — ADR-033 phase 5, the surface over `package-panel-state`.
 *
 * Every row is a measurement or a choice (design note §4): READINESS is the
 * composer's own refusal list rendered as rows and repeated verbatim as the
 * Package button's disabled reason; IDENTITY shows the id the roster gave
 * and what it replaces; USE IN WORLD is two ticks plus the avatar picker's
 * four filters (unset = the replaced row's, said); SELECTION LOOP and the
 * ANIMATION PREFERENCES are the family's pool badged shipped/staged, with ▶
 * playing the clip on the character (phase 4) and `pin` writing a binding.
 * Choices commit as a whole block through `package.set`; the panel decides
 * nothing and never writes the Document itself.
 *
 * The library is read on the first render (prod, same origin) and a bake
 * folder can be opened from here; a failed read is a row, not a blank.
 *
 * A pure view over `deps`, the Zones/Character posture: capability asked at
 * render, subject replaceable. Depends on: panel-registry (types),
 * package-panel-state, value-row, kernel (band constants). Depended on by:
 * main.
 */
import {
  HEIGHT_METERS_BAND,
  PICKER_FAMILIES,
  PICKER_KINDS,
  PICKER_PRESENTATIONS,
  PICKER_STYLES,
  type ClipBadge,
  type PackageChoices,
} from '@harryhausen/kernel';
import type { PanelMount } from './panel-registry.js';
import {
  packagePanelView,
  withFacets,
  withHeight,
  withIdentity,
  withNpc,
  togglePresentation,
  withPickerFacets,
  withPin,
  withRole,
  withSelectionClip,
  withoutPin,
  withoutHeroTake,
  withHeroFavourite,
  stabilizeId,
  type PackagePanelInputs,
  type PackagePanelView,
} from './package-panel-state.js';
import { createValueRow } from './value-row.js';

export interface PackagePanelDeps {
  /** The inputs the view is computed from — asked at render time. */
  readonly inputs: () => PackagePanelInputs;
  /** Kick the library read if it has not happened; the panel re-renders on `subscribeLibrary`. */
  readonly ensureLibrary: () => void;
  readonly subscribeLibrary: (listener: () => void) => () => void;
  /** Open a bake folder (a directory picker, or a file-input fallback) — main re-reads the library with it. */
  readonly openBakeFolder: () => Promise<void>;
  /** Commit a whole choices block through the command log. */
  readonly setChoices: (choices: PackageChoices) => void;
  /** Play a library clip on the character (phase 4), or null to stop. Refusals reach the status line. */
  readonly preview: (clipId: string | null) => Promise<void>;
  /** The clipId previewing right now, or null. */
  readonly previewing: () => string | null;
  /** 📷 — capture the viewport at this frame as a new take (phase 6); refusals reach the status line. */
  readonly takeHero: () => Promise<void>;
  /** 📷 headshot — World's opening shot (ADR-035): framed from the declared height and the head bone, the live camera untouched. */
  readonly takeHeadshot: () => Promise<void>;
  /** A URL the panel can show for a take this session holds, or null when the bytes are gone. */
  readonly takeUrl: (name: string) => string | null;
  /** Show or hide the 1:1 crop guide over the viewport while the panel is mounted. */
  readonly showHeroGuide: (on: boolean) => void;
  /** ⇪ — the folder door (phase 7): the picker at the gesture, then the package; refusals reach the status line. */
  readonly package: () => Promise<void>;
  /** ⇪ all — the batch door (ADR-035): a source folder with its roster sheet, a destination, one body at a time; a second press cancels. */
  readonly packageAll: () => Promise<void>;
  /** Is a batch running now? The button reads as a cancel while it is. */
  readonly batchRunning: () => boolean;
  /** The source folder armed by the first press (its name), or null — the second press picks the destination and runs. */
  readonly batchArmed: () => string | null;
}

const BODIES = ['human', 'monster', 'quadruped'] as const;
const PRESENTATIONS = ['feminine', 'masculine', 'neutral'] as const;

function h3(text: string): HTMLHeadingElement {
  const el = document.createElement('h3');
  el.textContent = text;
  return el;
}
function p(className: string, text: string): HTMLParagraphElement {
  const el = document.createElement('p');
  el.className = className;
  el.textContent = text;
  return el;
}
function button(className: string, text: string, title: string, onClick: () => void): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = className;
  el.textContent = text;
  el.title = title;
  el.addEventListener('click', onClick);
  return el;
}
function textField(value: string, placeholder: string, onCommit: (value: string) => void): HTMLInputElement {
  const el = document.createElement('input');
  el.type = 'text';
  el.className = 'vr-field pkg-text';
  el.value = value;
  el.placeholder = placeholder;
  el.addEventListener('change', () => onCommit(el.value));
  return el;
}
function select<T extends string>(options: readonly { value: T; label: string; disabled?: boolean; group?: string }[], value: T | '', onChange: (v: T) => void, title: string): HTMLSelectElement {
  const el = document.createElement('select');
  el.className = 'vr-field pkg-select';
  el.title = title;
  // Options carrying a `group` are shown under an <optgroup> in first-seen order; bare ones sit at the top.
  const groups = new Map<string, HTMLOptGroupElement>();
  for (const o of options) {
    const opt = document.createElement('option');
    opt.value = o.value;
    opt.textContent = o.label;
    opt.disabled = o.disabled === true;
    if (o.group === undefined) {
      el.append(opt);
      continue;
    }
    let g = groups.get(o.group);
    if (g === undefined) {
      g = document.createElement('optgroup');
      g.label = o.group;
      groups.set(o.group, g);
      el.append(g);
    }
    g.append(opt);
  }
  el.value = value;
  el.addEventListener('change', () => onChange(el.value as T));
  return el;
}
function labelled(text: string, control: HTMLElement): HTMLLabelElement {
  const el = document.createElement('label');
  el.className = 'pkg-field';
  const span = document.createElement('span');
  span.className = 'vr-label';
  span.textContent = text;
  el.append(span, control);
  return el;
}
function badgeText(shipping: 'shipped' | 'staged'): string {
  return shipping === 'shipped' ? '✓ shipped' : '◌ staged';
}
/** A clip's loop, said on the option — a walk or an idle that does not close its loop reads as a limp. */
function loopText(loopable: boolean | 'unknown' | undefined): string {
  return loopable === true ? '↻ loops' : loopable === false ? '✗ no loop' : '? loop unmeasured';
}

export function createPackagePanel(deps: PackagePanelDeps): PanelMount {
  return (container, store) => {
    const section = document.createElement('section');
    section.className = 'package-panel';
    const expanded = new Map<string,boolean>();
    const drawer = (key:string,title:string,open=false) => {
      const node=document.createElement('details');node.className='pkg-drawer';node.open=expanded.get(key) ?? open;
      const summary=document.createElement('summary');summary.textContent=title;node.append(summary);
      node.addEventListener('toggle',()=>{if(node.isConnected) expanded.set(key,node.open);});
      return node;
    };
    let heightRow: ReturnType<typeof createValueRow> | null = null;

    const render = (): void => {
      section.replaceChildren();
      deps.ensureLibrary();
      const inputs = deps.inputs();
      const v = packagePanelView(inputs);
      // Every commit carries the roster's id once the roster has spoken (Codex P1, #152).
      const commit = (next: PackageChoices): void => deps.setChoices(stabilizeId(v, next));
      const previewing = deps.previewing();

      // ── READINESS ──────────────────────────────────────────────────────────
      const readiness = document.createElement('section');
      readiness.className = 'hand-card zone-card pkg-card';
      readiness.append(h3('Package for poqpoq World'));
      for (const row of v.readiness) {
        const line = p(row.ok ? 'hand-reading pkg-ok' : 'hand-refusal pkg-not', `${row.ok ? '●' : '○'} ${row.label}  ${row.detail}`);
        readiness.append(line);
      }
      if (v.library.status === 'read') {
        for (const e of v.library.errors) readiness.append(p('hand-refusal', `library: ${e}`));
        for (const r of v.library.reasons) readiness.append(p('hand-refusal', `pool: ${r}`));
      }
      const libRow = document.createElement('div');
      libRow.className = 'hand-recenter zone-row';
      libRow.append(
        button('hand-revert', v.library.bakeName === null ? 'Bake folder…' : `Bake: ${v.library.bakeName}`, 'open a local bake folder (manifest.json + sets.json + clips) as the pool — badged against prod', () => {
          void deps.openBakeFolder();
        }),
      );
      readiness.append(libRow);
      section.append(readiness);

      // ── IDENTITY ───────────────────────────────────────────────────────────
      const identity = document.createElement('section');
      identity.className = 'hand-card zone-card pkg-card';
      identity.append(h3('Character details'));
      const technical=drawer('identity','More details');technical.classList.add('pkg-technical');
      identity.append(labelled('name', textField(v.identity.name, inputs.doc.name, (name) => commit(withIdentity(v.choices, { name })))));
      const idField = textField(v.choices.id ?? (v.identity.idSource === 'roster' ? (v.identity.id ?? '') : ''), v.identity.id ?? 'id', (id) => commit(withIdentity(v.choices, { id })));
      technical.append(labelled('id', idField));
      technical.append(
        p(
          v.identity.rosterProblem === null ? 'hand-reading' : 'hand-refusal',
          v.identity.rosterProblem ??
            (v.identity.id === null
              ? 'no id — the name leaves nothing to make one from'
              : v.identity.idSource === 'roster'
                ? `id ${v.identity.id} — replaces "${v.identity.replaces?.name ?? ''}" on the roster (same name, same slot)`
                : v.identity.idSource === 'chosen'
                  ? `id ${v.identity.id} — yours${v.identity.replaces !== null ? ` (replaces ${v.identity.replaces.id})` : ''}`
                  : `id ${v.identity.id} — from the name; a new roster row`),
        ),
      );
      identity.append(labelled('artist', textField(v.identity.artist, 'who made it', (artist) => commit(withIdentity(v.choices, { artist })))));
      technical.append(labelled('tags', textField(v.identity.tags.join(', '), 'comma-separated', (tags) => commit(withIdentity(v.choices, { tags: tags.split(',') })))));
      const facets = document.createElement('div');
      facets.className = 'hand-recenter zone-row';
      facets.append(
        labelled(
          'body',
          select(
            BODIES.map((b) => ({ value: b, label: b })),
            v.identity.facets.body,
            (body) => commit(withFacets(v.choices, { body })),
            'the body facet World resolves sets by (mc/<body>/…)',
          ),
        ),
        labelled(
          'presentation',
          select(
            PRESENTATIONS.map((x) => ({ value: x, label: x })),
            v.identity.facets.presentation,
            (presentation) => commit(withFacets(v.choices, { presentation })),
            'the presentation facet (mc/<body>/<presentation>/<id>)',
          ),
        ),
      );
      technical.append(facets);
      heightRow = createValueRow({
        label: 'Height',
        unit: 'm',
        // The band is (min, max]: the first step above the open floor, never a wider gap (Codex P2, #152).
        min: HEIGHT_METERS_BAND.min + 0.01,
        max: HEIGHT_METERS_BAND.max,
        step: 0.01,
        precision: 2,
        tip: 'World scales the body to this height',
        describe: () =>
          `World scales the avatar so it stands this tall (delivery §2a). ${
            v.identity.heightMeters.provenance === 'default' ? `The default, ${v.identity.heightMeters.value.toFixed(2)} m, is what World scales every avatar to today — a default is a declaration, not an absence.` : 'Declared by you.'
          }`,
        state: () => ({ kind: 'ready', value: v.identity.heightMeters.value, chip: v.identity.heightMeters.provenance === 'declared' ? 'declared' : 'default' }),
        commit: (metres) => commit(withHeight(v.choices, metres)),
        // Nothing to preview: World scales the body to this; Harry shows it at its authored size.
        preview: () => undefined,
      });
      heightRow.render(); // the field, the chip and the slider take the package's value — never the browser's default (Codex P2, #152 round four)
      identity.append(heightRow.root, heightRow.refusal);
      technical.append(p('hand-reading', `entity path ${v.identity.entityPath ?? '(none)'}`));
      identity.append(technical);
      section.append(identity);

      // ── USE IN WORLD ───────────────────────────────────────────────────────
      const use = document.createElement('section');
      use.className = 'hand-card zone-card pkg-card';
      use.append(h3('Use in World'));
      const roles = document.createElement('div');
      roles.className = 'hand-recenter zone-row';
      for (const role of ['avatar', 'npc'] as const) {
        const lab = document.createElement('label');
        lab.className = 'pkg-check';
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.checked = v.choices.roles.includes(role);
        box.addEventListener('change', () => commit(withRole(v.choices, role, box.checked)));
        lab.append(box, document.createTextNode(role === 'avatar' ? ' offer as avatar' : ' available as NPC'));
        roles.append(lab);
      }
      use.append(roles);
      if (v.choices.roles.includes('npc')) {
        use.append(
          labelled(
            'archetype',
            textField(v.choices.npc?.archetype ?? '', 'npc/humanoid/civilian', (archetype) => commit(withNpc(v.choices, { archetype, ...(v.choices.npc?.note !== undefined ? { note: v.choices.npc.note } : {}) }))),
          ),
          labelled('note', textField(v.choices.npc?.note ?? '', 'market vendor', (note) => commit(withNpc(v.choices, v.choices.npc === undefined ? null : { archetype: v.choices.npc.archetype, note })))),
        );
        if (v.choices.npc === undefined) use.append(p('hand-refusal', 'the npc role needs an archetype — World’s npcEntityPath vocabulary'));
      }
      // World's avatar picker filters (World #2125). Shown as the record will
      // carry them: the author's, else the replaced row's — editing one makes it yours.
      const picker = v.identity.picker.facets;
      const pres = document.createElement('div');
      pres.className = 'hand-recenter zone-row';
      for (const x of PICKER_PRESENTATIONS) {
        const lab = document.createElement('label');
        lab.className = 'pkg-check';
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.checked = picker.presentation?.includes(x) ?? false;
        box.addEventListener('change', () => {
          commit(withPickerFacets(v.choices, { presentation: togglePresentation(picker.presentation, x, box.checked) }));
        });
        lab.append(box, document.createTextNode(` ${x}`));
        pres.append(lab);
      }
      use.append(labelled('picker', pres));
      const pickerRow = document.createElement('div');
      pickerRow.className = 'hand-recenter zone-row';
      const closed = (values: readonly string[], current: string | undefined) => [
        { value: '', label: '—' },
        ...[...values, ...(current !== undefined && !values.includes(current) ? [current] : [])].map((x) => ({ value: x, label: x })),
      ];
      pickerRow.append(
        labelled('kind', select(closed(PICKER_KINDS, picker.kind), picker.kind ?? '', (kind) => commit(withPickerFacets(v.choices, { kind })), 'the picker’s kind filter')),
        labelled('style', select(closed(PICKER_STYLES, picker.style), picker.style ?? '', (style) => commit(withPickerFacets(v.choices, { style })), 'the picker’s style filter')),
      );
      use.append(pickerRow);
      const fam = textField((picker.family ?? []).join(', '), PICKER_FAMILIES.slice(0, 3).join(', ') + '…', (family) => commit(withPickerFacets(v.choices, { family: family.split(',') })));
      fam.title = `comma-separated; World's families: ${PICKER_FAMILIES.join(', ')}`;
      use.append(labelled('families', fam));
      if (v.identity.picker.carried.length > 0) {
        use.append(p('hand-reading', `${v.identity.picker.carried.join(', ')} kept from ${v.identity.replaces?.name ?? 'the roster row'} — change one to make it yours`));
      }
      section.append(use);

      // ── SELECTION LOOP ─────────────────────────────────────────────────────
      const loop = document.createElement('section');
      loop.className = 'hand-card zone-card pkg-card';
      loop.append(h3('Selection animation'));
      if (v.selectionLoop.candidates.length === 0) {
        loop.append(p('hand-refusal', v.selectionLoop.reason ?? 'no candidates'));
      } else {
        const row = document.createElement('div');
        row.className = 'hand-recenter zone-row';
        const chosen = v.selectionLoop.chosen ?? '';
        row.append(
          select(
            [
              { value: '', label: '— choose the loop the carousel plays —' },
              ...v.selectionLoop.candidates.map((c) => ({ value: c.clipId, label: `${c.clipId} · ${c.group} · ${badgeText(c.shipping)}` })),
            ],
            chosen,
            (clipId) => commit(withSelectionClip(v.choices, clipId === '' ? undefined : clipId)),
            'loopable clips of the family pool (idle and social first)',
          ),
        );
        if (chosen !== '') {
          const on = previewing === chosen;
          row.append(
            button('hand-graft-now', on ? '■ stop' : '▶ loop', on ? 'stop the preview' : 'play this loop on the character', () => {
              void deps.preview(on ? null : chosen);
            }),
          );
        }
        loop.append(row);
        const badge = v.selectionLoop.candidates.find((c) => c.clipId === chosen);
        if (badge !== undefined && badge.shipping === 'staged') loop.append(p('hand-refusal', `${chosen} is staged, not on prod yet — World cannot play it until the bake deploys`));
      }
      section.append(loop);

      // ── HERO CARD (phase 6) ────────────────────────────────────────────────
      const hero = document.createElement('section');
      hero.className = 'hand-card zone-card pkg-card';
      hero.append(h3('Portrait'));
      const takeRow = document.createElement('div');
      takeRow.className = 'hand-recenter zone-row';
      takeRow.append(
        button('hand-revert pkg-take', '📷 Take', 'capture the viewport at this frame — the square guide is the crop; the character alone, transparent around it', () => {
          void deps.takeHero();
        }),
        button('hand-revert pkg-take', 'Reshoot portrait', 'A fresh neutral pose with consistent head framing and lighting. Selects the new portrait for the card.', () => {
          void deps.takeHeadshot();
        }),
        p('hand-reading', v.choices.heroTakes.length === 0 ? 'Reshoot a consistently framed portrait, or use Take for your current view.' : `${v.choices.heroTakes.length} take(s) · ★ = the card`),
      );
      hero.append(takeRow);
      if (v.choices.heroTakes.length > 0) {
        const takes = document.createElement('div');
        takes.className = 'pkg-takes';
        for (const name of v.choices.heroTakes) {
          const card = document.createElement('figure');
          card.className = `pkg-take${v.choices.heroFavourite === name ? ' pkg-take-star' : ''}`;
          const url = deps.takeUrl(name);
          if (url !== null) {
            const img = document.createElement('img');
            img.src = url;
            img.alt = name;
            img.width = 96;
            img.height = 96;
            card.append(img);
          } else {
            card.append(p('hand-refusal pkg-take-gone', 'no PNG in this session'));
          }
          const cap = document.createElement('figcaption');
          cap.append(
            button('hand-revert', v.choices.heroFavourite === name ? '★' : '☆', v.choices.heroFavourite === name ? `${name} is the card` : `make ${name} the card`, () => {
              commit(withHeroFavourite(v.choices, name));
            }),
            document.createTextNode(` ${name} `),
            button('hand-revert', '✕', `lift ${name}`, () => {
              commit(withoutHeroTake(v.choices, name));
            }),
          );
          card.append(cap);
          takes.append(card);
        }
        hero.append(takes);
      }
      section.append(hero);

      // ── ANIMATION PREFERENCES ──────────────────────────────────────────────
      const prefs = drawer('animations','Animation overrides');
      prefs.className = 'hand-card zone-card pkg-card';
      prefs.append(h3(`Animation preferences${v.identity.facets.presentation !== 'neutral' ? ` · default (${v.identity.facets.presentation})` : ''}`));
      if (v.preferences.length === 0) {
        prefs.append(p('hand-refusal', v.rig === null ? (v.library.status === 'unread' ? 'library not read yet' : (v.library.reasons[0] ?? 'no pool for this rig')) : 'nothing to inherit — the family has no sets document and no contract keys'));
      }
      for (const row of v.preferences) {
        const line = document.createElement('div');
        line.className = 'hand-recenter zone-row pkg-pref';
        const name = document.createElement('span');
        name.className = 'vr-label pkg-key';
        name.textContent = `${row.key}${row.state !== 'neutral' ? ` (${row.state})` : ''}${row.npcOnly ? ' · npc only' : ''}`;
        if (row.npcOnly && !v.choices.roles.includes('npc')) name.title = 'a combat preference — it ships only with the npc role; tick the role or lift the pin';
        const what = document.createElement('span');
        what.className = 'hand-reading pkg-what';
        what.textContent =
          row.pin !== undefined
            ? `pinned ${row.pin.clips.join(', ')}${row.inherited === null ? ' · World inherits nothing for this key here today' : ''}`
            : row.inherited === null
              ? 'nothing inherited'
              : `${row.inherited.binding.clips.length === 1 ? row.inherited.binding.clips[0] : `pool of ${row.inherited.binding.clips.length}`} · ${row.inherited.source === 'sets' ? row.inherited.binding.selector : 'contract key'}`;
        line.append(name, what);
        if (row.sample !== null) {
          const on = previewing === row.sample;
          line.append(
            button('hand-revert', on ? '■' : '▶', on ? 'stop' : `play ${row.sample} on the character`, () => {
              void deps.preview(on ? null : row.sample);
            }),
          );
        }
        // Paired first: the row's own kind (walks for walk, idles for idle), then the
        // group's other actions, then everything else — all still reachable.
        const kind = row.candidates.kind;
        const kindLabel = kind === null ? 'this key' : kind.action !== null ? `${kind.action} (${kind.group})` : kind.group;
        const option = (c: ClipBadge, group: string) => ({ value: c.clipId, label: `${c.clipId} · ${loopText(c.record.loopable)} · ${badgeText(c.shipping)}`, group });
        const pinSelect = select(
          [
            { value: '', label: row.pin !== undefined ? 'unpin (inherit)' : 'pin…' },
            ...row.candidates.matched.map((c) => option(c, `${kindLabel} — ${row.candidates.matched.length}`)),
            ...row.candidates.sameGroup.map((c) => option(c, `other ${kind?.group ?? ''} — ${row.candidates.sameGroup.length}`)),
            ...row.candidates.rest.map((c) => option(c, `everything else — ${row.candidates.rest.length}`)),
          ],
          row.pin?.clips[0] ?? '',
          (clipId) => commit(clipId === '' ? withoutPin(v.choices, row.key, row.state) : withPin(v.choices, row.key, row.state, clipId)),
          'replace the pool for this avatar with one clip (an ADR-189 binding under its own entity path)',
        );
        line.append(pinSelect);
        prefs.append(line);
      }
      section.append(prefs);

      // ── PACKAGE… (the folder door, phase 7) ────────────────────────────────
      const door = document.createElement('section');
      door.className = 'hand-card zone-card pkg-card pkg-door';
      const pkgBtn = button('hand-graft-now', '⇪ Package…', v.ready ? "choose where — a folder <id>/ with avatar.json, <id>.glb and hero/<id>.png lands inside it (World's ingest reads exactly that)" : 'disabled — see the reasons', () => {
        void deps.package();
      });
      pkgBtn.disabled = !v.ready;
      door.append(pkgBtn);
      // The batch door (ADR-035 §3.3): needs the library, not this subject's
      // readiness — each body brings its own row. Disabled with the reason
      // until the library is read; a second press while running cancels
      // between bodies.
      const running = deps.batchRunning();
      const armed = deps.batchArmed();
      const allBtn = button(
        'hand-revert pkg-take',
        running ? '⏹ Stop after this body' : armed !== null ? `⇪ Package all: destination for "${armed}"…` : '⇪ Package all…',
        running
          ? 'the body in flight finishes and the batch stops; the report and the filled sheet are written'
          : armed !== null
            ? `the source is armed — this press picks the destination folder and runs every packageable row of "${armed}"`
            : "first press: pick the source folder (bodies + roster-sheet.json, Chrome/Edge); second press: pick a destination and run — every packageable row is opened, its clip played at frame zero, its headshot taken, its folder written; refusals go in batch-report.json",
        () => {
          void deps.packageAll();
        },
      );
      allBtn.disabled = false; // the door itself reads the library and refuses with the reason
      const batch=drawer('batch','Batch packaging');batch.append(allBtn);
      if (v.reasons.length > 0) {
        const ul = document.createElement('ul');
        ul.className = 'hand-refusal pkg-reasons';
        for (const r of v.reasons) {
          const li = document.createElement('li');
          li.textContent = r;
          ul.append(li);
        }
        door.append(ul);
      } else {
        door.append(p('hand-already', `ready — ⇪ writes ${v.identity.id ?? '<id>'}/ (avatar.json · ${v.identity.id ?? '<id>'}.glb · hero/${v.identity.id ?? '<id>'}.png) where you point it`));
      }
      // Warnings sit with the reasons but never block (a new picker family).
      for (const w of v.warnings) door.append(p('hand-reading', `⚠ ${w}`));
      const diagnostics=drawer('diagnostics','Technical details');diagnostics.append(readiness);
      const presentation=document.createElement('section');presentation.className='pkg-presentation';
      presentation.append(hero,loop);
      // One predictable reading order. Actual blockers stay beside the final action.
      section.replaceChildren(identity,use,presentation,prefs,diagnostics,batch,door);
    };

    container.append(section);
    const unsubscribeStore = store.subscribe(render);
    const unsubscribeLibrary = deps.subscribeLibrary(render);
    render();
    deps.showHeroGuide(true); // the crop is visible while the panel is: what 📷 will take

    return {
      dispose(): void {
        deps.showHeroGuide(false);
        unsubscribeStore();
        unsubscribeLibrary();
        heightRow = null;
        section.remove();
      },
    };
  };
}

export type { PackagePanelView };
